import { useEffect, useRef } from 'react';
import { usePermission } from '@/hooks/usePermission';
import { useAuthStore } from '@/stores/authStore';
import { useProductContentStore } from '@/stores/productContentStore';
import { useTaskStore } from '@/stores/taskStore';

/** Fallback refresh cadence, only while the live socket is down (TTL still gates each call). */
const SUMMARY_POLL_MS = 120_000;

/**
 * Keeps the product-content draft summary warm for AppTopBar while in a team shell.
 *
 * Updates when:
 * - You stage / discard / commit locally (store updates immediately)
 * - Anyone changes the queue, or a commit finishes: the live socket says so (TaskLiveConnector)
 * - Only while that socket is down: on focus / tab visible, and every ~2 minutes (if stale)
 *
 * `fetchSummary` uses a 20s TTL so route remounts and focus events do not spam the API.
 */
export function ProductContentQueueBootstrap() {
  const canView = usePermission('PRODUCT_CONTENT_VIEW');
  const teamId = useAuthStore((s) => s.user?.active_team?.id);
  const live = useTaskStore((s) => s.connected);
  const prevTeamIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      prevTeamIdRef.current !== undefined &&
      prevTeamIdRef.current !== teamId
    ) {
      useProductContentStore.getState().reset();
    }
    prevTeamIdRef.current = teamId;
  }, [teamId]);

  useEffect(() => {
    if (!canView || !teamId) return;

    const refreshIfVisible = (force = false) => {
      if (document.visibilityState === 'hidden') return;
      void useProductContentStore.getState().fetchSummary({ force });
    };

    // Respect TTL on remounts. After a team switch, reset() clears the TTL clock
    // so this still loads once for the new store.
    refreshIfVisible(false);
    // Live updates arrive over the socket; no need to poll while it's connected.
    if (live) return;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfVisible(false);
    };
    const onFocus = () => refreshIfVisible(false);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    const intervalId = window.setInterval(
      () => refreshIfVisible(false),
      SUMMARY_POLL_MS
    );

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(intervalId);
    };
  }, [canView, teamId, live]);

  return null;
}
